// Types for the external Reviewer Recommendation Service.
//
// Request:  POST {REVIEWER_RECOMMENDATION_API}/providers/{provider}/recommendations
//           body = IReviewerRecommendationRequest
// Response: { recommendations: ReviewerCandidate[] }
//           NOTE: the service does not echo back submissionId — the caller
//           attaches it when building IReviewerRecommendationResponse.

export enum RecommendationProvider {
  PROPHY = 'PROPHY',
  GLOBAL_CAMPUS = 'GLOBAL_CAMPUS'
}

export type IReviewerRecommendationProvider = {
  provider: RecommendationProvider;
  enable: boolean;
};

/** Normalized conflict-of-interest flags for a reviewer candidate. */
export interface ReviewerConflicts {
  /** Co-authored with a submission author. */
  coauthor?: boolean;
  /** Shares an affiliation with a submission author. */
  affiliation?: boolean;
  /** Has retracted works. */
  retracted?: boolean;
  /** Is referenced by the submission. */
  referenced?: boolean;
  /** Externally flagged conflict of interest. */
  external?: boolean;
}

/**
 * The provider-agnostic reviewer candidate returned by every recommendation
 * provider. Providers map their own response into this shape so consumers get
 * a single, stable contract regardless of source.
 */
export interface ReviewerCandidate {
  /** Stable upstream identifier (e.g. Prophy author id or OpenAlex author id). */
  id: string;
  /** Full display name. */
  name: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  /** Bare ORCID identifier (e.g. `0000-0001-8001-0126`). */
  orcid?: string;
  email?: string;
  /** Primary/current affiliation. */
  affiliation?: string;
  /** Relevance score; the scale is provider-specific. */
  score?: number;
  hIndex?: number;
  /** Number of publications/works. */
  worksCount?: number;
  citationsCount?: number;
  url?: string;
  /** Topics/keywords associated with the candidate. */
  concepts?: string[];
  conflicts?: ReviewerConflicts;
  /** Provider-specific data not captured by the standard fields above. */
  extra?: Record<string, unknown>;
}

export type IReviewerRecommendationArticle = {
  id: number;
  title: string;
  url: string;
  year: number;
};

export type IReviewerRecommendationResponse = {
  recommendations: ReviewerCandidate[];
};

export type IReviewerRecommendationAuthor = {
  name: string;
  affiliation: string;
  email: string | null;
  orcid: string | null;
};

export type IReviewerRecommendationRequest = {
  submissionId: string;
  journalId: string;
  title: string;
  abstract: string;
  authors: IReviewerRecommendationAuthor[];
  folder: string;
  providers: RecommendationProvider[];
};

export type IReviewerCOIMessage = {
  message: string;
  isAllowed: boolean;
};

// A candidate enriched with the local COI/eligibility checks
// (see LexiconServices.getReviewerStatuses).
export type IReviewerSuggestion = ReviewerCandidate & {
  coi?: {
    error: IReviewerCOIMessage[];
    warning: IReviewerCOIMessage[];
    info: IReviewerCOIMessage[];
    isAllowed: boolean;
  };
};

export type IReviewerRecommendationRecord = {
  submissionId: string;
  submissionContextId: string;
  reviewers: Array<IReviewerSuggestion>;
  status: 'completed' | 'failed';
  provider: string;
  errorMessage?: string;
  createdAt?: Date;
  updatedAt?: Date;
};
